'use client';

import { colors } from '@/constant/colors';
import { useEffect, useState } from 'react';

interface AnimatedBarProps {
  progress: number; // 0 to 1
  height?: number;
  backgroundColor?: string;
  progressColor?: string;
  className?: string;
  comboCount?: number;
}

const AnimatedBar = ({ 
  progress, 
  height = 13,
  backgroundColor = '#F8F5EE',
  progressColor = '#42C52D',
  className = '',
  comboCount = 0
}: AnimatedBarProps) => {
  const [showFirework, setShowFirework] = useState(false);
  const [prevProgress, setPrevProgress] = useState(progress);
  const [showComboAnimation, setShowComboAnimation] = useState(false);
  const [prevComboCount, setPrevComboCount] = useState(comboCount);

  // Detect progress changes and trigger firework animation
  useEffect(() => {
    if (progress > prevProgress) {
      setShowFirework(true);
      setTimeout(() => setShowFirework(false), 1500);
    }
    setPrevProgress(progress);
  }, [progress, prevProgress]);

  // Detect combo count changes and trigger bounce animation
  useEffect(() => {
    if (comboCount > 2 && comboCount !== prevComboCount) {
      setShowComboAnimation(true);
      setTimeout(() => setShowComboAnimation(false), 2000);
    }
    setPrevComboCount(comboCount);
  }, [comboCount, prevComboCount]);

  // Determine actual progress color based on comboCount
  const actualProgressColor = comboCount >= 5 ? '#F19B38' : progressColor;

  return (
    <div 
      className={`relative rounded-full ${className}`}
      style={{ 
        backgroundColor,
        height: `${height}px`,
        minHeight: `${height}px`, // Prevent height collapse
        maxHeight: `${height}px`  // Prevent height expansion
      }}
    >
      <div 
        className="h-full rounded-full flex justify-center items-center overflow-hidden" 
        style={{ 
          backgroundColor: actualProgressColor,
          width: `${progress * 100}%`,
          transition: 'width 0.8s cubic-bezier(0.1, 1.2, 0.8, 1), background-color 0.3s ease' // Spring curve for width, smooth transition for color
        }}
      >
        {/* Enhanced highlight effect with multiple layers */}
        <div className="absolute top-0 left-0 w-full h-[50%] bg-gradient-to-b from-white/50 to-transparent rounded-t-full" />
        <div className="absolute top-0 left-0 w-full h-[30%] bg-gradient-to-b from-white/40 to-transparent rounded-t-full blur-[1px]" />
        
      </div>
      {/* Firework SVG animation at the end - positioned outside the progress bar to prevent layout shifts */}
      {showFirework && progress > 0 && (
        <div 
          className="absolute w-32 h-32 pointer-events-none z-50" 
          style={{ 
            left: `calc(${progress * 100}% - 64px)`,
            top: '-64px', // Fixed position to prevent layout shifts
          }}
        >
          <svg width="128" height="128" viewBox="0 0 128 128" className="animate-pulse">
            {/* Central burst - larger lines */}
            <g className="animate-spin" style={{ transformOrigin: '64px 64px', animationDuration: '1.2s' }}>
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
                <line
                  key={angle}
                  x1="64"
                  y1="64"
                  x2="64"
                  y2="16"
                  stroke={actualProgressColor}
                  strokeWidth="4"
                  strokeLinecap="round"
                  transform={`rotate(${angle} 64 64)`}
                  style={{
                    animation: 'expandLarge 1s ease-out forwards',
                    opacity: 1
                  }}
                />
              ))}
            </g>
            
            {/* Additional colored bursts */}
            <g style={{ transformOrigin: '64px 64px', animation: 'rotate 2s linear infinite' }}>
              {[0, 60, 120, 180, 240, 300].map((angle) => (
                <line
                  key={`color-${angle}`}
                  x1="64"
                  y1="64"
                  x2="64"
                  y2="24"
                  stroke={actualProgressColor === '#F19B38' ? '#FFB84D' : '#5ED344'}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  transform={`rotate(${angle + 30} 64 64)`}
                  style={{
                    animation: 'expandLarge 1s ease-out forwards',
                    animationDelay: '0.2s',
                    opacity: 0.9
                  }}
                />
              ))}
            </g>
            
            {/* Larger sparkles */}
            {[[32, 24], [96, 24], [32, 104], [96, 104], [24, 64], [104, 64]].map(([x, y], i) => (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="6"
                fill={i % 2 === 0 ? actualProgressColor : (actualProgressColor === '#F19B38' ? '#FFD700' : '#42C52D')}
                style={{
                  animation: 'sparkle 1.5s ease-out forwards',
                  animationDelay: `${i * 0.1}s`,
                  opacity: 0
                }}
              />
            ))}
            
            {/* Central glow */}
            <circle
              cx="64"
              cy="64"
              r="12"
              fill="#FFF"
              opacity="0.8"
              style={{
                animation: 'glow 1s ease-out forwards'
              }}
            />
          </svg>
          
          <style jsx>{`
            @keyframes expandLarge {
              0% {
                stroke-dasharray: 0 30;
                opacity: 1;
                transform: scale(0.5);
              }
              50% {
                stroke-dasharray: 15 30;
                opacity: 1;
                transform: scale(1.2);
              }
              100% {
                stroke-dasharray: 30 30;
                opacity: 0;
                transform: scale(1.5);
              }
            }
            
            @keyframes sparkle {
              0% {
                opacity: 0;
                transform: scale(0) translate(0, 0);
              }
              50% {
                opacity: 1;
                transform: scale(1) translate(var(--tx, 0), var(--ty, 0));
              }
              100% {
                opacity: 0;
                transform: scale(0.5) translate(calc(var(--tx, 0) * 2), calc(var(--ty, 0) * 2));
              }
            }
            
            @keyframes glow {
              0% {
                r: 8;
                opacity: 1;
              }
              100% {
                r: 32;
                opacity: 0;
              }
            }
            
            @keyframes rotate {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
            
            @keyframes shine {
              0% {
                transform: translateX(-100%) skewX(-12deg);
              }
              100% {
                transform: translateX(200%) skewX(-12deg);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default AnimatedBar;